import styled from '@emotion/styled'
import { Form, Formik, useFormikContext } from 'formik'
import gql from 'graphql-tag'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import toastr from 'toastr'
import { useQuery } from '../../../../api/graphql'
import useOnImageUploadStateChange from '../../../../hooks/useOnImageUploadStateChange'
import useRecordSubmissionMutation from '../../../../hooks/useRecordSubmissionMutation'
import useUserOrganizations, { UserOrganizationsQuery } from '../../../../hooks/useUserOrganizations'
import { CurrentUserQuery } from '../../../../providers/CurrentUserContext'
import Routes from '../../../../startup/routes'
import Grid, { GridItem, LARGE, breakpoint } from '../../../../styles/Grid'
import { inputFieldHeight } from '../../../../styles/vars'
import OrganizationSelector from '../../events/OrganizationSelector'
import Button from '../../form-fields/Button'
import MediaUploadField from '../../form-fields/MediaUploadField'
import RichTextEditor from '../../form-fields/RichTextEditor'
import StringInput from '../../form-fields/StringInput'
import PasswordEdit from './PasswordEdit'

export const ProfileFormQuery = gql`
  query ProfileFormQuery {
    currentUser {
      id
      firstName
      lastName
      email
      signature
      profilePic_publicId: profilePicPublicId
      isCurator
      isAuthor
      defaultOrganization {
        id
      }
    }
  }
`

type DefaultOrganizerSectionProps = { organizations: { id: string; name: string; hasActiveSubscription: boolean }[] }
const DefaultOrganizerSection: React.FC<DefaultOrganizerSectionProps> = ({ organizations }) => {
  const { values, setFieldValue } = useFormikContext<{ defaultOrganizationId: string | null }>()
  const isOrganizationSelected = !!values.defaultOrganizationId

  return (
    <StyledDefaultOrganizerSection>
      <StyledSectionHeader>Default organizer</StyledSectionHeader>
      <StyledSectionDescription>
        Pre-fill the &quot;Who is organizing this event?&quot; field on new events. You can still change it per event.
      </StyledSectionDescription>

      <StyledRadioGroup>
        <StyledRadioLabel>
          <input
            type="radio"
            checked={!isOrganizationSelected}
            onChange={() => setFieldValue('defaultOrganizationId', null)}
          />
          Myself
        </StyledRadioLabel>
        <StyledRadioLabel>
          <input
            type="radio"
            checked={isOrganizationSelected}
            onChange={() => setFieldValue('defaultOrganizationId', organizations[0].id)}
          />
          An organization
        </StyledRadioLabel>
      </StyledRadioGroup>

      {isOrganizationSelected && (
        <OrganizationSelector
          organizations={organizations}
          value={values.defaultOrganizationId}
          onChange={orgId => setFieldValue('defaultOrganizationId', orgId)}
        />
      )}
    </StyledDefaultOrganizerSection>
  )
}

const ProfileEdit: React.FC = () => {
  const { data, loading } = useQuery(ProfileFormQuery)
  const { organizations, loading: orgsLoading } = useUserOrganizations()

  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const onUploadingStateChange = useOnImageUploadStateChange(setIsUploadingAvatar)
  const { replace: redirectTo } = useHistory()

  const successNotification = () => toastr.success('Your profile is updated!')

  const handleSuccessfulUpdate = () => {
    successNotification()
    redirectTo(Routes.dashWelcome())
  }

  const { handleSubmit: sendProfileData, mutationLoading: recordUpdateMutationLoading } = useRecordSubmissionMutation({
    query: PROFILE_EDIT,
    queryName: 'profileUpdate',
    recordName: 'profile',
    onSuccess: handleSuccessfulUpdate,
    submitOptions: {
      refetchQueries: () => [{ query: CurrentUserQuery }, { query: UserOrganizationsQuery }],
      awaitRefetchQueries: true,
    },
  })

  const updateProfile = formData => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { profilePic = '', ...profileData } = { ...formData, profilePicPublicId: formData.profilePic?.publicId }
    sendProfileData(profileData)
  }

  if (loading || orgsLoading) return null

  const { currentUser } = data
  const canSubmitStories = currentUser?.isAuthor

  const { firstName, lastName, signature, profilePic, email, defaultOrganization } = currentUser
  const initialDefaultOrgId = defaultOrganization?.id || null

  return (
    <StyledFormBox>
      <StyledFormHeader>
        <h1>Account Settings</h1>
      </StyledFormHeader>

      <Formik
        onSubmit={updateProfile}
        initialValues={{
          firstName,
          lastName: lastName || '',
          signature,
          profilePic,
          defaultOrganizationId: initialDefaultOrgId,
        }}
        validate={values => {
          const errors: { firstName?: string } = {}

          if (!values.firstName) errors.firstName = 'First name is required'

          return errors
        }}
      >
        <Form>
          <Grid>
            <GridItem columns={12} medium={6}>
              <StringInput name="firstName" placeholder="Your first name" />
            </GridItem>

            <GridItem columns={12} medium={6}>
              <StringInput name="lastName" placeholder="Your last name" />
            </GridItem>
          </Grid>

          {canSubmitStories && <RichTextEditor name="signature" />}

          <StyledAvatarUpload>
            <MediaUploadField name="profilePic" allowVideo={false} folder="users" onUploadingStateChange={onUploadingStateChange} />
          </StyledAvatarUpload>

          {organizations.length > 0 && <DefaultOrganizerSection organizations={organizations} />}

          <StyledProfileSubmitButton
            disabled={recordUpdateMutationLoading || isUploadingAvatar}
            shouldShowLoadingSpinner={recordUpdateMutationLoading}
            text="Save"
          />
        </Form>
      </Formik>

      <PasswordEdit email={email} />
    </StyledFormBox>
  )
}

export default ProfileEdit

const PROFILE_EDIT = gql`
  mutation ProfileUpdate($input: ProfileUpdateInput!) {
    profileUpdate(input: $input) {
      profile {
        id
        firstName
        lastName
        email
        signature
      }
      errors {
        attribute
        message
      }
    }
  }
`

const FORM_MAX_WIDTH = 552
const FORM_EDGE_DISTANCE_MOBILE = 20
const FORM_EDGE_DISTANCE_LARGE = 60

const StyledFormBox = styled.div`
  width: 100%;
  max-width: ${FORM_MAX_WIDTH + FORM_EDGE_DISTANCE_MOBILE * 2}px;
  padding: ${FORM_EDGE_DISTANCE_MOBILE}px;
  margin: 0 auto;

  ${breakpoint(LARGE)} {
    max-width: ${FORM_MAX_WIDTH + FORM_EDGE_DISTANCE_LARGE * 2}px;
    padding: ${FORM_EDGE_DISTANCE_LARGE}px;
    margin: 0;
  }
`

const StyledFormHeader = styled.div`
  margin-bottom: 40px;
  color: #102638;
`

const StyledAvatarUpload = styled.div`
  margin-top: 40px;
  max-width: 267px;
  height: 165px;
`

const StyledDefaultOrganizerSection = styled.div`
  margin-top: 40px;
`

const StyledSectionHeader = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #102638;
  margin-bottom: 4px;
`

const StyledSectionDescription = styled.p`
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 12px;
`

const StyledRadioGroup = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 12px;
`

const StyledRadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #111827;
  cursor: pointer;

  input[type='radio'] {
    margin: 0;
  }
`

const StyledProfileSubmitButton = styled(Button)`
  display: inline-block;
  background-color: #6fbf53;
  color: white;
  width: 164px;
  height: ${inputFieldHeight};
  margin-top: 40px;
  margin-left: auto;
  margin-right: auto;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 19px;
  text-transform: none;
  border-radius: 4px;
  padding: 0.625em 1.75em;
  text-align: center;
  opacity: 1;
  transition: all 0.15s ease-out;
  cursor: pointer;
`
